import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { SessionService } from '../../../core/services/session.service';
import {
  PaperBannerComponent,
  PaperButtonComponent,
} from '@ricoh-paper/angular';
import { TranslocoPipe } from '@jsverse/transloco';

/**
 * Tiny top-of-screen banner that appears when the API is unreachable.
 *
 * RESPONSIBILITY:
 *   Render a one-line "We can't reach the server" notice + a Retry button
 *   when SessionService.apiOnline() is false for longer than GRACE_MS.
 *   Auto-dismisses immediately when the API comes back. Also auto-retries
 *   quietly on a small backoff so the user doesn't have to click Retry the
 *   moment the dev server restarts.
 *
 * SIGNALS:
 *   visible         — debounced: true only after apiOnline has been false
 *                     for GRACE_MS (prevents flash on transient blips)
 *   retrying        — true while a manual or auto retry is in flight
 *
 * AI CONTEXT:
 *   - Designed to coexist with whatever surface is mounted underneath.
 *     Always rendered in the app shell; CSS controls visibility.
 *   - Replaces the previous behaviour where a network blip would log the
 *     user out (see graphql.providers.ts ErrorLink + session.service).
 *   - The banner intentionally does NOT block the UI. Users keep their
 *     cached data and can keep navigating; only fresh requests fail.
 */
@Component({
  selector: 'paper-connection-banner',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PaperBannerComponent, PaperButtonComponent, TranslocoPipe],
  templateUrl: './paper-connection-banner.component.html',
  styleUrl: './paper-connection-banner.component.scss',
})
export class PaperConnectionBannerComponent {
  private readonly session = inject(SessionService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly onPublicPreview = signal(
    window.location.hash.startsWith('#/preview/'),
  );

  /** Wait this long before showing the banner so transient blips
   *  (e.g. first Knowledge load) don't flash a scary message. */
  private static readonly GRACE_MS = 2_000;

  private readonly _visible = signal(false);
  /** Public preview harnesses are intentionally backend-free and synthetic;
   *  an unreachable banner there is expected noise, not a user outage. */
  protected readonly visible = computed(
    () => this._visible() && !this.onPublicPreview(),
  );
  protected readonly retrying = signal(false);

  private graceTimer: ReturnType<typeof setTimeout> | null = null;

  /** Backoff schedule for the auto-retry loop (ms). Stops escalating after
   *  ~30s — by then the user is aware and can hit Retry manually if they
   *  haven't already. */
  private static readonly BACKOFF_MS = [3_000, 6_000, 12_000, 24_000, 30_000];
  private autoRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private autoRetryAttempt = 0;

  constructor() {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.onPublicPreview.set(
            window.location.hash.startsWith('#/preview/'),
          );
        },
        // Angular Router event streams do not error in normal operation.
        error: () => {},
      });
    effect(() => {
      const online = this.session.apiOnline();
      if (online) {
        this.clearGraceTimer();
        this._visible.set(false);
        this.cancelAutoRetry();
      } else {
        this.startGraceTimer();
        this.scheduleAutoRetry();
      }
    });
  }

  private startGraceTimer(): void {
    if (this.graceTimer) return;
    this.graceTimer = setTimeout(() => {
      this.graceTimer = null;
      if (!this.session.apiOnline()) this._visible.set(true);
    }, PaperConnectionBannerComponent.GRACE_MS);
  }

  private clearGraceTimer(): void {
    if (this.graceTimer) {
      clearTimeout(this.graceTimer);
      this.graceTimer = null;
    }
  }

  private scheduleAutoRetry(): void {
    this.cancelAutoRetry();
    const backoff = PaperConnectionBannerComponent.BACKOFF_MS;
    // Math.min clamps to the array bounds — fall back to the last
    // backoff entry rather than '!' (and 1000ms if BACKOFF_MS is empty,
    // which is contractually unreachable).
    const idx = Math.min(this.autoRetryAttempt, backoff.length - 1);
    const delay = backoff[idx] ?? backoff[backoff.length - 1] ?? 1000;
    this.autoRetryTimer = setTimeout(() => {
      this.autoRetryAttempt++;
      void this.attemptReconnect(false);
    }, delay);
  }

  private cancelAutoRetry(): void {
    if (this.autoRetryTimer) {
      clearTimeout(this.autoRetryTimer);
      this.autoRetryTimer = null;
    }
    this.autoRetryAttempt = 0;
  }

  protected async retryNow(): Promise<void> {
    await this.attemptReconnect(true);
  }

  private async attemptReconnect(manual: boolean): Promise<void> {
    if (this.retrying()) return;
    this.retrying.set(true);
    try {
      const ok = await this.session.recheckConnection();
      if (!ok && !manual) this.scheduleAutoRetry();
    } finally {
      this.retrying.set(false);
    }
  }
}
